import { isErrorOfVsApi } from '@vs/rtk/utils';

import { EntityControllerGetEntityApiResponse } from './vsEntity2ApiInjected';
import { vsEntity2ApiManualInjected } from './vsEntity2ApiManualInjected';

export enum VsEntity2SliceTags {
  USER = 'USER',
  POLICY = 'POLICY',
  POLICIES_OF_USER = 'POLICIES_OF_USER',
  ENTITY_SYNC = 'ENTITY_SYNC',
  ENTITY_INFO = 'ENTITY_INFO',
  ENTITY_LIST = 'ENTITY_LIST',
  DOMAIN_LIST = 'DOMAIN_LIST',
  DOMAIN_USER_LIST = 'DOMAIN_USER_LIST',
  ADMIN_ENTITY_LIST = 'ADMIN_ENTITY_LIST',
  ADMIN_ENTITY_DETAIL_BASIC_INFO = 'ADMIN_ENTITY_DETAIL_BASIC_INFO',
  ADMIN_ENTITY_DETAIL_PROFILE = 'ADMIN_ENTITY_DETAIL_PROFILE',
  ADMIN_ENTITY_DETAIL_DOMAIN = 'ADMIN_ENTITY_DETAIL_DOMAIN',
  ADMIN_ENTITY_DETAIL_USERS = 'ADMIN_ENTITY_DETAIL_USERS',
  ADMIN_ENTITY_SIGN_UP_LIST = 'ADMIN_ENTITY_SIGN_UP_LIST',
  ADMIN_ENTITY_SIGN_UP_DETAIL = 'ADMIN_ENTITY_SIGN_UP_DETAIL',
  CREATE_SIGNUP_ENTITY_LIST = 'CREATE_SIGNUP_ENTITY_LIST',
  SEND_SIGNUP_ENTITY_LIST = 'SEND_SIGNUP_ENTITY_LIST',
  ENTITY_AUTO_APPROVER_LIST = 'ENTITY_AUTO_APPROVER_LIST',
  ENTITY_COUNTRY_SUPER_ADMIN = 'ENTITY_COUNTRY_SUPER_ADMIN',
  ENTITY_REGION_SUPER_ADMIN = 'ENTITY_REGION_SUPER_ADMIN',
  NFC_ACCOUNT_LIST = 'NFC_ACCOUNT_LIST',
  NFC_CARD_LIST = 'NFC_CARD_LIST',
}

const vsEntity2ApiEnhanced = vsEntity2ApiManualInjected.enhanceEndpoints({
  addTagTypes: Object.values(VsEntity2SliceTags),
  endpoints: {
    nfcControllerList: {
      providesTags: [VsEntity2SliceTags.NFC_ACCOUNT_LIST],
    },
    nfcControllerListCards: {
      providesTags: [VsEntity2SliceTags.NFC_CARD_LIST],
    },
    nfcControllerBind: {
      invalidatesTags: [
        VsEntity2SliceTags.NFC_ACCOUNT_LIST,
        VsEntity2SliceTags.NFC_CARD_LIST,
      ],
    },
    nfcControllerUnbind: {
      invalidatesTags: [
        VsEntity2SliceTags.NFC_ACCOUNT_LIST,
        VsEntity2SliceTags.NFC_CARD_LIST,
      ],
    },
    nfcControllerClear: {
      invalidatesTags: [
        VsEntity2SliceTags.NFC_ACCOUNT_LIST,
        VsEntity2SliceTags.NFC_CARD_LIST,
      ],
    },
    nfcControllerBatchBind: {
      invalidatesTags: [
        VsEntity2SliceTags.NFC_ACCOUNT_LIST,
        VsEntity2SliceTags.NFC_CARD_LIST,
      ],
    },
    entityControllerGetEntity: {
      providesTags: [VsEntity2SliceTags.ENTITY_INFO],
      extraOptions: {
        isFallbackableError(error) {
          if (isErrorOfVsApi(error)) {
            if (
              error.data.status_code === 400 &&
              typeof error.data.message === 'string' &&
              error.data.message.includes('not in this entity')
            ) {
              return true;
            }
            if (
              error.data.status_code === 500 &&
              typeof error.data.message === 'string' &&
              error.data.message.includes('invalid input syntax for type uuid')
            ) {
              return true;
            }
          }
          return false;
        },
        fallbackResponse: {
          id: '',
          name: '',
          main_domain: '',
          generics_code: '',
          domain_type_code: '',
          area: '',
          create_time: '',
          modify_time: '',
          profile: {
            contact_email: '',
            contact_phone: '',
            owner_id: '',
            zipcode: '',
            country_code: '',
            country: '',
            region: '',
            city: '',
            address: '',
            create_time: '',
            modify_time: '',
            given_name: '',
            family_name: '',
          },
        } as EntityControllerGetEntityApiResponse,
      },
    },

    entityControllerGetUserEntityList: {
      providesTags: [VsEntity2SliceTags.ENTITY_LIST],
    },

    entityUserControllerCreate: {
      invalidatesTags: [
        VsEntity2SliceTags.USER,
        VsEntity2SliceTags.ADMIN_ENTITY_DETAIL_USERS,
      ],
    },
    entityUserControllerRemoveUser: {
      invalidatesTags: [
        VsEntity2SliceTags.USER,
        VsEntity2SliceTags.ADMIN_ENTITY_DETAIL_USERS,
      ],
    },

    signupRequestControllerGetInviteEntityRequestList: {
      providesTags: [VsEntity2SliceTags.SEND_SIGNUP_ENTITY_LIST],
    },
    signupRequestControllerGetEntityRequestList: {
      providesTags: [VsEntity2SliceTags.CREATE_SIGNUP_ENTITY_LIST],
    },
    signupRequestControllerCreateSignUpRequest: {
      invalidatesTags: [VsEntity2SliceTags.CREATE_SIGNUP_ENTITY_LIST],
    },
    entityControllerUpdateEntityOwner: {
      invalidatesTags: [
        VsEntity2SliceTags.ENTITY_INFO,
        VsEntity2SliceTags.ADMIN_ENTITY_DETAIL_BASIC_INFO,
      ],
    },
    entityControllerUpdateEntity: {
      invalidatesTags: [
        VsEntity2SliceTags.ENTITY_INFO,
        VsEntity2SliceTags.ADMIN_ENTITY_DETAIL_BASIC_INFO,
        VsEntity2SliceTags.ADMIN_ENTITY_DETAIL_DOMAIN,
      ],
    },
    // The default entity is not a field of its own in the responses: the entity
    // list marks it with is_current and the entity info is fetched for it, so
    // both go stale when the default moves and neither refetches on its own.
    entityControllerSetEntityDefault: {
      invalidatesTags: [
        VsEntity2SliceTags.ENTITY_LIST,
        VsEntity2SliceTags.ENTITY_INFO,
      ],
    },
    entityManagementControllerGetEntityList: {
      providesTags: [VsEntity2SliceTags.ADMIN_ENTITY_LIST],
    },
    managementEntityControllerDeleteEntity: {
      invalidatesTags: [VsEntity2SliceTags.ADMIN_ENTITY_LIST],
    },

    signupManagementControllerGetEntityRequestListForAdmin: {
      providesTags: [VsEntity2SliceTags.ADMIN_ENTITY_SIGN_UP_LIST],
    },
    signupManagementControllerGetInviteEntityRequestForAdmin: {
      providesTags: [VsEntity2SliceTags.ADMIN_ENTITY_SIGN_UP_DETAIL],
    },
    signupManagementControllerGetEntityRequestForAdmin: {
      providesTags: [VsEntity2SliceTags.ADMIN_ENTITY_SIGN_UP_DETAIL],
    },
    signupRequestControllerApprove: {
      invalidatesTags: [
        VsEntity2SliceTags.ADMIN_ENTITY_SIGN_UP_LIST,
        VsEntity2SliceTags.ADMIN_ENTITY_SIGN_UP_DETAIL,
      ],
    },
    signupRequestControllerDeleteEntityRequest: {
      invalidatesTags: [
        VsEntity2SliceTags.ADMIN_ENTITY_SIGN_UP_LIST,
        VsEntity2SliceTags.ADMIN_ENTITY_SIGN_UP_DETAIL,
      ],
    },
    entityUserControllerPolicyInfo: {
      providesTags: [VsEntity2SliceTags.POLICIES_OF_USER],
    },
    regionManagerManagementControllerGetRegionManagerList: {
      providesTags: [VsEntity2SliceTags.ENTITY_REGION_SUPER_ADMIN],
    },
    regionManagerManagementControllerCreateRegionManager: {
      invalidatesTags: [VsEntity2SliceTags.ENTITY_REGION_SUPER_ADMIN],
    },
    regionManagerManagementControllerDeleteRegionManager: {
      invalidatesTags: [VsEntity2SliceTags.ENTITY_REGION_SUPER_ADMIN],
    },
    regionManagerManagementControllerUpdateRegionManager: {
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const argsOfQuery =
            vsEntity2ApiEnhanced.util.selectCachedArgsForQuery(
              getState(),
              'regionManagerManagementControllerGetRegionManagerList'
            );
          dispatch(
            vsEntity2ApiEnhanced.util.updateQueryData(
              'regionManagerManagementControllerGetRegionManagerList',
              argsOfQuery[0],
              draft => {
                const targetIndex = draft.data.findIndex(
                  item => item.region === arg.region
                );
                if (targetIndex !== -1) {
                  draft.data[targetIndex] = {
                    ...draft.data[targetIndex],
                    ...(arg.updateRegionManagerRequestDto as any),
                  };
                }
              }
            )
          );
        } catch (error) {
          //
        }
      },
    },
    countryManagerManagementControllerGetCountryManagerList: {
      providesTags: [VsEntity2SliceTags.ENTITY_COUNTRY_SUPER_ADMIN],
    },
    countryManagerManagementControllerCreateCountryManager: {
      invalidatesTags: [VsEntity2SliceTags.ENTITY_COUNTRY_SUPER_ADMIN],
    },
    countryManagerManagementControllerDeleteCountryManager: {
      invalidatesTags: [VsEntity2SliceTags.ENTITY_COUNTRY_SUPER_ADMIN],
    },
    countryManagerManagementControllerUpdateCountryManager: {
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const argsOfQuery =
            vsEntity2ApiEnhanced.util.selectCachedArgsForQuery(
              getState(),
              'countryManagerManagementControllerGetCountryManagerList'
            );
          dispatch(
            vsEntity2ApiEnhanced.util.updateQueryData(
              'countryManagerManagementControllerGetCountryManagerList',
              argsOfQuery[0],
              draft => {
                const targetIndex = draft.data.findIndex(
                  item => item.country === arg.country
                );
                if (targetIndex !== -1) {
                  draft.data[targetIndex] = {
                    ...draft.data[targetIndex],
                    ...(arg.updateCountryManagerRequestDto as any),
                  };
                }
              }
            )
          );
        } catch (error) {
          //
        }
      },
    },
  },
});

export * from './vsEntity2ApiInjected';
export { vsEntity2ApiEnhanced as vsEntity2ApiSlice };

export const {
  useAzureControllerGetTenantInfiniteGroupsInfiniteQuery,
  useAzureControllerGetTenantInfiniteSubGroupsInfiniteQuery,
  useGSuiteControllerGetDomainInfiniteGroupsInfiniteQuery,
  useGSuiteControllerGetDomainInfiniteSubGroupsInfiniteQuery,
} = vsEntity2ApiManualInjected;

export type DomainVerifyType = 'mx' | 'txt';

export enum EntitySignupRequestStatus {
  NEW = 'new',
  SUCCESS = 'success',
  REJECT = 'reject',
}

export enum EntitySignupRequestSendStatus {
  NEW = 'new',
  EXPIRED = 'expired',
  SUBMITTED = 'submitted',
  REJECTED = 'rejected',
  SUCCESS = 'success',
  PENDING = 'pending',
}
